import type { EnumUnion } from '@dresscode-frontend/utility-types';

/**
 * 親タブのキー。MegaMenu の最上位タブを表す。
 *
 * - `IN_USE` / `IN_INVENTORY` は複数の CategoryKey を内包する 2 段構成タブ
 * - その他は単一 CategoryKey に 1:1 で対応する
 */
export const TabKey = {
  IN_USE: 'IN_USE',
  IN_INVENTORY: 'IN_INVENTORY',
  UNDER_REPAIR: 'UNDER_REPAIR',
  IN_DISTRIBUTION: 'IN_DISTRIBUTION',
  ALL: 'ALL',
  DECOMMISSIONED: 'DECOMMISSIONED',
} as const;
export type TabKey = EnumUnion<typeof TabKey>;

/**
 * デバイス一覧 v4 のカテゴリキー。
 * URL クエリの `category` と API リクエストの category 値に対応する。
 */
export const CategoryKey = {
  IN_USED: 'IN_USED',
  IN_PLACED: 'IN_PLACED',
  IN_STOCK: 'IN_STOCK',
  IN_TROUBLED: 'IN_TROUBLED',
  REFURBISHING: 'REFURBISHING',
  UNDER_REPAIR: 'UNDER_REPAIR',
  IN_DISTRIBUTION: 'IN_DISTRIBUTION',
  ALL: 'ALL',
  DECOMMISSIONED: 'DECOMMISSIONED',
} as const;
export type CategoryKey = EnumUnion<typeof CategoryKey>;

/**
 * MegaMenu 内の表示専用キー（URL / API の category には使わない）。
 * `findSelected` が実カテゴリと衝突しないように親行・All 行に用いる。
 */
export const DeviceInventoryMegaMenuPanelKey = {
  IN_USE_SUMMARY: 'IN_USE_SUMMARY',
  IN_USE_ALL_ROW: 'IN_USE_ALL_ROW',
  IN_INVENTORY_SUMMARY: 'IN_INVENTORY_SUMMARY',
  IN_INVENTORY_ALL_ROW: 'IN_INVENTORY_ALL_ROW',
} as const;
export type DeviceInventoryMegaMenuPanelKey = EnumUnion<
  typeof DeviceInventoryMegaMenuPanelKey
>;

/** URL / API に載せる CategoryKey のみ true */
export function isDeviceInventoryCategoryKey(
  value: string,
): value is CategoryKey {
  return (Object.values(CategoryKey) as string[]).includes(value);
}

export type DeviceInventoryMegaMenuValue =
  | CategoryKey
  | DeviceInventoryMegaMenuPanelKey;

/**
 * CategoryKey から TabKey を導出するマッピング。
 * URL の category を変更した際に、対応する親タブをアクティブ表示するために使用する。
 */
export const CATEGORY_TO_TAB_KEY: Record<CategoryKey, TabKey> = {
  [CategoryKey.IN_USED]: TabKey.IN_USE,
  [CategoryKey.IN_PLACED]: TabKey.IN_USE,
  [CategoryKey.IN_STOCK]: TabKey.IN_INVENTORY,
  [CategoryKey.IN_TROUBLED]: TabKey.IN_INVENTORY,
  [CategoryKey.REFURBISHING]: TabKey.IN_INVENTORY,
  [CategoryKey.UNDER_REPAIR]: TabKey.UNDER_REPAIR,
  [CategoryKey.IN_DISTRIBUTION]: TabKey.IN_DISTRIBUTION,
  [CategoryKey.ALL]: TabKey.ALL,
  [CategoryKey.DECOMMISSIONED]: TabKey.DECOMMISSIONED,
};

/**
 * TabKey から、その親タブに属する CategoryKey のリストを取得する。
 * MegaMenu のタブクリック時に、デフォルトで表示する子カテゴリを決めるために使用する。
 */
export const TAB_KEY_TO_CATEGORIES: Record<TabKey, readonly CategoryKey[]> = {
  [TabKey.IN_USE]: [CategoryKey.IN_USED, CategoryKey.IN_PLACED],
  [TabKey.IN_INVENTORY]: [
    CategoryKey.IN_STOCK,
    CategoryKey.IN_TROUBLED,
    CategoryKey.REFURBISHING,
  ],
  [TabKey.UNDER_REPAIR]: [CategoryKey.UNDER_REPAIR],
  [TabKey.IN_DISTRIBUTION]: [CategoryKey.IN_DISTRIBUTION],
  [TabKey.ALL]: [CategoryKey.ALL],
  [TabKey.DECOMMISSIONED]: [CategoryKey.DECOMMISSIONED],
};
